import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import SiteLogo from './SiteLogo';
import {
  Phone, Mail, MapPin, CheckCircle, AlertTriangle, AlertCircle, Info,
  ArrowRight, Menu, X, ChevronDown, Users2, ClipboardCheck,
  CreditCard, Globe, BarChart3, MessageCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mirrors the feature showcase sections right after the landing-page hero
const CORE_FEATURES = [
  { title: 'Biometric Attendance', desc: 'Fingerprint attendance with WhatsApp alerts', icon: ClipboardCheck, to: '/#attendance' },
  { title: 'Smart Fee Management', desc: 'Installments, dues and receipts', icon: CreditCard, to: '/#fees' },
  { title: 'AI Agent & Lead CRM', desc: 'From enquiry to admission, automated', icon: Users2, to: '/#crm' },
  { title: 'Meta WhatsApp', desc: 'Approved templates and parent alerts', icon: MessageCircle, to: '/#whatsapp' },
  { title: 'Multi-branch ERP', desc: 'Every branch on one dashboard', icon: Globe, to: '/#multi-branch' },
  { title: 'All features', desc: 'LMS, exams, mobile app and more', icon: BarChart3, to: '/features' },
];

const NAV_LINKS = [
  { label: 'Pricing', to: '/#pricing' },
];

const linkClass = 'text-[15px] font-medium text-white/85 hover:text-brand-gold transition-colors';

export default function Navbar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [cmsContent, setCmsContent] = useState(null);
  const [popularCities, setPopularCities] = useState([]);

  useEffect(() => {
    axios.get(`${API}/marketplace/landing-content`)
      .then(r => setCmsContent(r.data))
      .catch(() => {});
    axios.get(`${API}/marketplace/popular-cities`)
      .then(r => setPopularCities(r.data || []))
      .catch(() => {});
  }, []);

  // Close the mobile menu whenever the route (or hash) changes
  useEffect(() => { setMenuOpen(false); }, [location.pathname, location.hash]);

  const phone = cmsContent?.footer?.phone || '+91 6260129453';
  const email = cmsContent?.footer?.email || 'contact@mycoachhub.in';
  const menuCities = popularCities.slice(0, 12);

  const isAnnouncementActive = () => {
    if (!cmsContent?.announcement_active || !cmsContent?.announcement) return false;
    const today = new Date().toISOString().split('T')[0];
    if (cmsContent.announcement_start_date && today < cmsContent.announcement_start_date) return false;
    if (cmsContent.announcement_end_date && today > cmsContent.announcement_end_date) return false;
    return true;
  };

  return (
    <>
      {isAnnouncementActive() && (
        <div className={`py-2.5 px-4 text-center text-sm font-medium ${
          cmsContent.announcement_type === 'success' ? 'bg-emerald-500 text-white' :
          cmsContent.announcement_type === 'warning' ? 'bg-brand-gold text-brand-ink' :
          cmsContent.announcement_type === 'alert' ? 'bg-red-500 text-white' :
          'bg-brand-navy text-white'
        }`}>
          <div className="flex items-center justify-center gap-2">
            {cmsContent.announcement_type === 'success' && <CheckCircle className="w-4 h-4" />}
            {cmsContent.announcement_type === 'warning' && <AlertTriangle className="w-4 h-4" />}
            {cmsContent.announcement_type === 'alert' && <AlertCircle className="w-4 h-4" />}
            {cmsContent.announcement_type === 'info' && <Info className="w-4 h-4" />}
            {cmsContent.announcement}
          </div>
        </div>
      )}

      {/* Top utility bar */}
      <div className="hidden md:block bg-brand-night text-white/80 text-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-2.5 flex items-center justify-between gap-6">
          <div className="flex items-center gap-7">
            <a href={`tel:${phone.replace(/\s/g, '')}`} className="inline-flex items-center gap-2 hover:text-white transition-colors" data-testid="topbar-phone">
              <Phone className="w-3.5 h-3.5 text-red-400" /> {phone}
            </a>
            <a href={`mailto:${email}`} className="inline-flex items-center gap-2 hover:text-white transition-colors" data-testid="topbar-email">
              <Mail className="w-3.5 h-3.5" /> {email}
            </a>
            <span className="hidden lg:inline-flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-red-400" /> Gwalior, India
            </span>
          </div>
          <div className="flex items-center gap-7">
            <span className="text-brand-gold font-medium">14-day free trial · No card required</span>
            <Link to="/page/support" className="hidden lg:inline-flex hover:text-white transition-colors">Help & Support</Link>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="sticky top-0 z-50 bg-brand-ink/95 backdrop-blur-md border-b border-white/10" aria-label="Main">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3.5 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center" data-testid="navbar-logo" aria-label="mycoachhub.in home">
            <SiteLogo variant="dark" iconOnlyOnMobile />
          </Link>

          <div className="hidden lg:flex items-center gap-9">
            {/* Features mega menu */}
            <div className="group relative">
              <Link to="/features" className={`${linkClass} inline-flex items-center gap-1 py-2`}>
                Features <ChevronDown className="w-3.5 h-3.5 opacity-70 group-hover:rotate-180 transition-transform" />
              </Link>
              <div className="invisible absolute left-1/2 top-full z-50 w-[620px] -translate-x-1/2 pt-4 opacity-0 transition-all group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-2xl shadow-brand-ink/20">
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-hover">Core features</p>
                      <p className="text-sm text-slate-500">What institutes use mycoachhub.in for</p>
                    </div>
                    <Link to="/features" className="text-sm font-semibold text-brand-navy hover:text-brand-gold-hover">All features →</Link>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {CORE_FEATURES.map(({ title, desc, icon: Icon, to }) => (
                      <Link
                        key={title}
                        to={to}
                        className="flex items-start gap-3 rounded-xl border border-slate-100 px-3 py-2.5 hover:border-brand-gold/40 hover:bg-brand-cream transition-colors"
                      >
                        <span className="w-9 h-9 rounded-lg bg-brand-ink text-brand-gold flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4" />
                        </span>
                        <span>
                          <span className="block text-sm font-semibold text-brand-ink">{title}</span>
                          <span className="block text-xs text-slate-500">{desc}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {NAV_LINKS.map((item) => (
              <Link key={item.label} to={item.to} className={linkClass}>{item.label}</Link>
            ))}

            {/* Institutes / locations menu */}
            <div className="group relative">
              <Link to="/institutes" className={`${linkClass} inline-flex items-center gap-1 py-2`}>
                Institutes <ChevronDown className="w-3.5 h-3.5 opacity-70 group-hover:rotate-180 transition-transform" />
              </Link>
              {menuCities.length > 0 && (
                <div className="invisible absolute left-1/2 top-full z-50 w-[560px] -translate-x-1/2 pt-4 opacity-0 transition-all group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                  <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-2xl shadow-brand-ink/20">
                    <div className="mb-4 flex items-center justify-between">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-hover">Locations</p>
                        <p className="text-sm text-slate-500">Browse institutes city by city</p>
                      </div>
                      <Link to="/institutes" className="text-sm font-semibold text-brand-navy hover:text-brand-gold-hover">All institutes →</Link>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {menuCities.map((cityItem) => (
                        <Link
                          key={cityItem.slug || cityItem.city}
                          to={`/coaching-in-${cityItem.slug || cityItem.city.toLowerCase().replace(/\s+/g, '-')}`}
                          className="rounded-xl border border-slate-100 px-3 py-2 hover:border-brand-gold/40 hover:bg-brand-cream transition-colors"
                        >
                          <p className="text-sm font-semibold text-brand-ink">{cityItem.city}</p>
                          <p className="text-xs text-slate-500">{cityItem.institute_count || 0} institutes</p>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Link to="/#contact" className={linkClass}>Contact</Link>
          </div>

          <div className="flex items-center gap-3">
            {user ? (
              <button onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard-btn"
                className="bg-brand-gold hover:bg-brand-gold-hover text-brand-ink text-base font-bold px-6 md:px-8 py-3 rounded-xl transition-colors shadow-[0_10px_30px_-10px_rgba(227,162,26,0.7)]">
                Dashboard
              </button>
            ) : (
              <>
                <Link to="/login" className="hidden md:block text-[15px] font-medium text-white/85 hover:text-white transition-colors" data-testid="nav-login-btn">Login</Link>
                <Link to="/register" data-testid="nav-register-btn"
                  className="group inline-flex items-center gap-1.5 bg-brand-gold hover:bg-brand-gold-hover text-brand-ink text-sm md:text-base font-bold px-4 md:px-6 py-2.5 md:py-3 rounded-xl transition-colors shadow-[0_10px_30px_-10px_rgba(227,162,26,0.7)]">
                  Start Free Trial <ArrowRight className="hidden sm:block w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </>
            )}
            <button
              className="lg:hidden p-2 text-white"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="lg:hidden border-t border-white/10 bg-brand-ink px-4 py-3">
            {[
              { label: 'Features', to: '/features' },
              ...NAV_LINKS,
              { label: 'Institutes', to: '/institutes' },
              { label: 'About Us', to: '/about' },
              { label: 'Contact', to: '/#contact' },
              ...(user ? [] : [{ label: 'Login', to: '/login' }]),
            ].map((item) => (
              <Link key={item.label} to={item.to} className="block py-3 text-base text-white/85 border-b border-white/5 last:border-0 hover:text-brand-gold">
                {item.label}
              </Link>
            ))}
            <div className="md:hidden pt-3 mt-1 space-y-2 text-sm text-white/60">
              <a href={`tel:${phone.replace(/\s/g, '')}`} className="flex items-center gap-2"><Phone className="w-4 h-4 text-brand-gold" /> {phone}</a>
              <a href={`mailto:${email}`} className="flex items-center gap-2"><Mail className="w-4 h-4 text-brand-gold" /> {email}</a>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
